import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Video, X } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { ADMIN_MENU } from "../../lib/menus";
import api, { apiError } from "../../lib/api";
import { mediaUrl } from "../../lib/format";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Pengaturan - DS KODE";
    api.get("/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/settings", form); toast.success("Pengaturan disimpan"); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!form) return <DashboardLayout title="Pengaturan" menu={ADMIN_MENU}><div className="h-64 skeleton rounded-xl" /></DashboardLayout>;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const uploadHeroVideo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("kind", "video");
    body.append("file", file);
    try {
      const response = await api.post("/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("hero_video", response.data.file_id);
      toast.success("Video hero berhasil diunggah");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <DashboardLayout title="Pengaturan" menu={ADMIN_MENU} testid="admin-settings">
      <form onSubmit={save} className="card p-6 space-y-4 max-w-xl">
        <h3 className="font-heading font-bold text-slate-900">Pengaturan Situs</h3>
        <div><label className="text-sm font-medium text-slate-700">Nama Situs</label><input value={form.site_name || ""} onChange={(e) => set("site_name", e.target.value)} className="input mt-1" data-testid="set-name" /></div>
        <div><label className="text-sm font-medium text-slate-700">Tagline</label><input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="input mt-1" /></div>
        <div className="border-t border-slate-100 pt-4">
          <h4 className="font-heading font-semibold text-slate-900 text-sm mb-3">Teks Hero (Halaman Utama)</h4>
          <div className="space-y-3.5">
            <div><label className="text-sm font-medium text-slate-700">Judul Hero</label><input value={form.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className="input mt-1" placeholder="Temukan Aplikasi & Source Code Siap Pakai" data-testid="set-hero-title" /></div>
            <div><label className="text-sm font-medium text-slate-700">Subjudul Hero</label><textarea rows={2} value={form.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className="input mt-1" placeholder="Solusi digital siap digunakan..." data-testid="set-hero-subtitle" /></div>
          </div>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <h4 className="font-heading font-semibold text-slate-900 text-sm mb-3">Video Animasi Hero</h4>
          {form.hero_video ? (
            <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-900">
              <video src={mediaUrl(form.hero_video)} muted loop autoPlay playsInline className="aspect-video w-full object-cover" />
              <button type="button" onClick={() => set("hero_video", "")} className="absolute right-2 top-2 rounded-full bg-slate-900/75 p-1.5 text-white" aria-label="Hapus video hero"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 py-8 text-slate-400 hover:border-indigo-300">
              <Video className="mb-2 h-7 w-7" />
              <span className="text-sm font-medium">Upload video MP4, WebM, atau OGG</span>
              <input type="file" accept="video/mp4,video/webm,video/ogg" onChange={uploadHeroVideo} className="hidden" data-testid="set-hero-video" />
            </label>
          )}
          <div className="mt-4">
            <label className="text-sm font-medium text-slate-700">Tampilan Video</label>
            <select value={form.hero_video_layout || "full"} onChange={(e) => set("hero_video_layout", e.target.value)} className="input mt-1" data-testid="set-hero-video-layout">
              <option value="full">Full layar</option>
              <option value="right-half">Setengah layar di kanan</option>
            </select>
          </div>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <h4 className="font-heading font-semibold text-slate-900 text-sm mb-3">Animasi Halaman Utama</h4>
          <div>
            <label className="text-sm font-medium text-slate-700">Animasi Hero</label>
            <select value={form.hero_animation || "fade-up"} onChange={(e) => set("hero_animation", e.target.value)} className="input mt-1" data-testid="set-hero-animation">
              <option value="fade-up">Fade Up</option>
              <option value="float">Float</option>
              <option value="glow">Glow</option>
              <option value="none">Tanpa Animasi</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-slate-700">Email Kontak</label><input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="input mt-1" /></div>
          <div><label className="text-sm font-medium text-slate-700">WhatsApp</label><input value={form.contact_whatsapp || ""} onChange={(e) => set("contact_whatsapp", e.target.value)} className="input mt-1" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!form.enable_downloads_count} onChange={(e) => set("enable_downloads_count", e.target.checked)} className="rounded" /> Tampilkan jumlah download</label>
        <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!form.enable_payments} onChange={(e) => set("enable_payments", e.target.checked)} className="rounded" /> Aktifkan sistem pembayaran (simulasi)</label>
        <button type="submit" disabled={busy} className="btn-primary" data-testid="set-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Pengaturan"}</button>
      </form>
    </DashboardLayout>
  );
}
